import {
  Anchor,
  Box,
  Button,
  PasswordInput,
  Stack,
  TextInput,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import {
  Lock,
  Mail,
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { useAuthStore } from '@store/auth.store'

import { validateEmailStructure } from '@common/utils/validation/authValidators'
import { AuthShell } from './AuthShell'

interface LoginFormValues {
  email: string
  password: string
}

const getEmailError = (email: string) => {
  if (!email) {
    return 'Email is required'
  }

  return validateEmailStructure(email) ? null : 'Incorrect email format'
}

const getPasswordError = (password: string) => {
  return password ? null : 'Password is required'
}

const Login = () => {
  const login = useAuthStore((state) => state.login)
  const loggingIn = useAuthStore((state) => state.loggingIn)
  const error = useAuthStore((state) => state.error)

  const form = useForm<LoginFormValues>({
    initialValues: {
      email: '',
      password: '',
    },
    transformValues: (values) => ({
      email: values.email.trim(),
      password: values.password,
    }),
    validate: {
      email: getEmailError,
      password: getPasswordError,
    },
  })

  const handleSubmit = async (values: LoginFormValues) => {
    await login(values.email, values.password)
  }

  return (
    <AuthShell
      title={'Welcome'}
      subtitle={
        <>
          Sign in to continue to
          <br />
          <b>SMART FLOW Monitoring</b>
        </>
    }
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap={'md'}>
          <TextInput
            label={'Email Address'}
            placeholder={'Enter email'}
            type={'email'}
            autoComplete={'email'}
            leftSection={<Mail size={16} />}
            required
            {...form.getInputProps('email')}
          />
          <PasswordInput
            label={'Password'}
            placeholder={'Enter password'}
            autoComplete={'current-password'}
            leftSection={<Lock size={16} />}
            required
            {...form.getInputProps('password')}
          />
          <Box ta={'right'}>
            <Anchor
              component={Link}
              to={'/forgot-password'}
              size={'sm'}
              fw={500}
              c={'blue.7'}
            >
              Forgot your password?
            </Anchor>
          </Box>
          <Button
            type={'submit'}
            loading={loggingIn && !error}
            disabled={!form.values.email || !form.values.password}
            fullWidth
            size={'md'}
            color={'blue'}
          >
            Login
          </Button>
        </Stack>
      </form>
    </AuthShell>
  )
}

export default Login
